import axios from "axios";

// The configured HTTP client — template §0.5: "Every network call goes through one configured client."
//
// `withCredentials` is REQUIRED: the backend authenticates with a JWT in an httpOnly cookie named
// `__session` (technical-foundation §6.2, frozen). The portal never holds or reads a token.
/** CS-5A — pages a customer reaches WITHOUT a session. */
// + Phase 8 (2026-10-02; owner ruling BD-3; MDS §37.4): the two public historical-application pages. A signed-in
// customer may open them too; being signed in grants nothing there (the applicant token does).
export const PUBLIC_AUTH_PATHS = ["/login", "/forgot-password", "/activate", "/reset-password", "/confirm-login-email",
  "/sign-up", "/application-status"];

// Part 3 BD-7: the one cookie holds a customer and a staff session side by side; this header tells the API the
// portal speaks for the CUSTOMER one, so a staff session in this browser is never used here. A plain link (a
// download) cannot send a header and carries `?vvr_aud=customer` instead.
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  withCredentials: true,
  timeout: 30000,
  headers: { "X-VVR-Audience": "customer" },
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const path = window.location.pathname;
      // A failed login is a legitimate 401 and must render inline, not redirect into a loop. CS-5A: the signed-out
      // pages (activation, reset, forgot, email confirmation) are also exempt — they are opened without a session,
      // and the boot-time /auth/me 401 must not throw the customer (and their link) onto the sign-in page.
      // Phase 8 (BD-3; MDS §37.4): on `/` the boot identity check answering 401 means "signed out", and `/` then shows
      // the public Home. Any OTHER 401 on `/` (an expired session on the Dashboard) still goes to sign-in as before.
      const signedOutHome = path === "/" && error.config && error.config.url === "/auth/me";
      if (!PUBLIC_AUTH_PATHS.includes(path) && !signedOutHome) window.location.href = "/login?session=expired";
    }
    // Always re-throw: the interceptor owns the global consequence, the caller owns the local one.
    return Promise.reject(error);
  }
);

export default api;

// ── Endpoints ───────────────────────────────────────────────────────────────
// EVERY path below was verified against the frozen routers, and every one is reachable by a CUSTOMER
// login — that is, it sits behind `requireAuthentication` rather than a designation or department
// gate. Nothing here is speculative, and no staff-only route appears.
//
// DELIBERATELY ABSENT, because a customer cannot reach them:
//   /reports/*          every Module 14 route is `staffGate`. No customer analytics endpoint exists
//                       anywhere in this system. See blocker B3.
//   /sales/payments     the payment LIST is `staffReadGate`; only `/sales/payments/:id` is open, and
//                       a customer holds no payment ids to ask for. See blocker B1.
//   POST /reallocations raising a reallocation is department-gated. Customers read only; the
//                       customer-facing path is a service request. See blocker B2.

export const auth = {
  // ⚠ RESOLVES TO `{ user, mustChangePassword }`, NOT TO THE USER. The account is nested under
  // `user`; spreading this payload as if it were the account silently yields `userType: undefined`.
  me: () => api.get("/auth/me").then((r) => r.data.data),
  // R&S Final Closure (owner FINAL 2026-09-28, §8): one field — a login mobile number or a login email.
  // CS6 Phase 8 follow-up (owner ruling RB-1): the portal's sign-in is for customer accounts — the server refuses
  // any other account before a session exists.
  login: (identifier, password) => api.post("/auth/login", { identifier, password, audience: "customer" }).then((r) => r.data),
  logout: () => api.post("/auth/logout").then((r) => r.data),
  changePassword: (currentPassword, newPassword) =>
    api.post("/auth/change-password", { currentPassword, newPassword }).then((r) => r.data),
};

// CS-5A — the signed-out credential flows (business/19 A-1/A-2 as amended). Tokens travel in the request BODY,
// never in a URL, and are never stored by the portal.
export const portalAuth = {
  inspectActivation: (token) => api.post("/auth/activation/inspect", { token }).then((r) => r.data.data),
  completeActivation: (body) => api.post("/auth/activation/complete", body).then((r) => r.data.data),
  requestNewInvitation: (email) => api.post("/auth/activation/request-new", { email }).then((r) => r.data.data),
  requestPasswordReset: (email) => api.post("/auth/password-reset/request", { email }).then((r) => r.data.data),
  inspectPasswordReset: (token) => api.post("/auth/password-reset/inspect", { token }).then((r) => r.data.data),
  completePasswordReset: (body) => api.post("/auth/password-reset/complete", body).then((r) => r.data.data),
  confirmLoginEmail: (token) => api.post("/auth/login-email/confirm", { token }).then((r) => r.data.data),
};

export const profile = {
  me: () => api.get("/customers/me").then((r) => r.data.data),
  portfolio: (id) => api.get(`/customers/${id}/portfolio`).then((r) => r.data.data),
  ownership: (id) => api.get(`/customers/${id}/ownership`).then((r) => r.data.data),
  myAssets: () => api.get("/customers/resolve/my-assets").then((r) => r.data.data),
};

export const bookings = {
  mine: () => api.get("/sales/bookings/me").then((r) => r.data.data),
  byId: (id) => api.get(`/sales/bookings/${id}`).then((r) => r.data.data),
  balance: (id) => api.get(`/sales/bookings/${id}/balance`).then((r) => r.data.data),
};

export const documents = {
  mine: (params) => api.get("/documents/my", { params }).then((r) => r.data.data),
  byId: (id) => api.get(`/documents/${id}`).then((r) => r.data.data),
  versions: (id) => api.get(`/documents/${id}/versions`).then((r) => r.data.data),
  // The stream-proxy download. Navigating to this URL sends the session cookie automatically.
  downloadUrl: (id) => `${import.meta.env.VITE_API_URL}/documents/${id}/download?vvr_aud=customer`,
  requestUpload: (body) => api.post("/documents/upload-authorizations", body).then((r) => r.data.data),
  confirmUpload: (id, body) =>
    api.post(`/documents/upload-authorizations/${id}/confirm`, body).then((r) => r.data),
};

export const support = {
  mine: (params) => api.get("/service-requests/me", { params }).then((r) => r.data.data),
  byId: (id) => api.get(`/service-requests/${id}`).then((r) => r.data.data),
  raise: (body) => api.post("/service-requests", body).then((r) => r.data),
  comment: (id, body) => api.post(`/service-requests/${id}/communications`, body).then((r) => r.data),
  confirm: (id, body) => api.post(`/service-requests/${id}/confirm`, body).then((r) => r.data),
  cancel: (id, body) => api.post(`/service-requests/${id}/cancel`, body).then((r) => r.data),
};

// CS6 Phase 9 (MDS CS6 §10.6, Addendum P9 §38.7–§38.8; P9-Q4) — the customer's request tracking. Every answer is the
// allow-listed projection, keyed by the customer-facing reference; the internal record id never reaches the browser.
export const csPortal = {
  requests: (params) => api.get("/cs-portal/requests", { params }).then((r) => r.data.data),
  request: (ref) => api.get(`/cs-portal/requests/${encodeURIComponent(ref)}`).then((r) => r.data.data.request),
  respond: (ref, body) => api.post(`/cs-portal/requests/${encodeURIComponent(ref)}/respond`, body).then((r) => r.data.data.request),
  confirm: (ref, body) => api.post(`/cs-portal/requests/${encodeURIComponent(ref)}/confirm`, body).then((r) => r.data.data.request),
  cancel: (ref, body) => api.post(`/cs-portal/requests/${encodeURIComponent(ref)}/cancel`, body).then((r) => r.data.data.request),
  // PART 8 (2026-10-08; MDS CS6 §48) — the guided NOC flow and the requester's NOC acts. Properties are named by their
  // My Properties key; the server resolves each against the session's own ownership.
  nocContext: (params) => api.get("/cs-portal/noc-requests/context", { params }).then((r) => r.data.data),
  createNoc: (body) => api.post("/cs-portal/noc-requests", body).then((r) => r.data.data),
  addDocuments: (ref, body) => api.post(`/cs-portal/requests/${encodeURIComponent(ref)}/documents`, body).then((r) => r.data.data.request),
  answerRequirement: (ref, number, body) =>
    api.post(`/cs-portal/requests/${encodeURIComponent(ref)}/requirements/${encodeURIComponent(number)}/respond`, body).then((r) => r.data.data.request),
  decideLegalIssue: (ref, body) => api.post(`/cs-portal/requests/${encodeURIComponent(ref)}/legal-issue`, body).then((r) => r.data.data.request),
  // PART 9 (2026-10-08; MDS CS6 §10.6) — the ONE guided reallocation flow (both entry points) and the owners' acts.
  reallocationContext: (params) => api.get("/cs-portal/reallocation-requests/context", { params }).then((r) => r.data.data),
  createReallocation: (body) => api.post("/cs-portal/reallocation-requests", body).then((r) => r.data.data),
  respondToReplacement: (ref, body) => api.post(`/cs-portal/requests/${encodeURIComponent(ref)}/reallocation/respond`, body).then((r) => r.data.data.request),
  answerResolution: (ref, body) => api.post(`/cs-portal/requests/${encodeURIComponent(ref)}/reallocation/resolution`, body).then((r) => r.data.data.request),
  addReallocationDocuments: (ref, body) => api.post(`/cs-portal/requests/${encodeURIComponent(ref)}/reallocation/documents`, body).then((r) => r.data.data.request),
};

/**
 * PART 1B (MDS CS6 §43.3) — "Register an Earlier Purchase": the signed-in customer's historical claim. The SAME
 * application as the public one; the session is the access (no tracking token), and the customer is always the
 * session's own — nothing here sends a customer id.
 */
const claim = (reference) => `/cs-portal/historical-applications/${encodeURIComponent(reference)}`;
const claimData = (r) => r.data.data;
export const earlierPurchase = {
  list: () => api.get("/cs-portal/historical-applications").then(claimData),
  create: (body = {}) => api.post("/cs-portal/historical-applications", body).then(claimData),
  get: (reference) => api.get(claim(reference)).then(claimData),
  save: (reference, body) => api.put(claim(reference), body).then(claimData),
  submit: (reference) => api.post(`${claim(reference)}/submit`, {}).then(claimData),
  resubmit: (reference) => api.post(`${claim(reference)}/resubmit`, {}).then(claimData),
  authorizeEvidence: (reference, body) => api.post(`${claim(reference)}/evidence/authorize`, body).then(claimData),
  confirmEvidence: (reference, evidenceId) => api.post(`${claim(reference)}/evidence/confirm`, { evidenceId }).then(claimData),
  removeEvidence: (reference, evidenceId) => api.delete(`${claim(reference)}/evidence/${encodeURIComponent(evidenceId)}`).then(claimData),
  respond: (reference, body) => api.post(`${claim(reference)}/requirements/respond`, body).then(claimData),
  identityDocuments: () => api.get("/cs-portal/identity-documents").then(claimData),
  setIdentityDocuments: (reference, documentIds) => api.put(`${claim(reference)}/identity-documents`, { documentIds }).then(claimData),
};

export const notifications = {
  list: (params) => api.get("/notifications", { params }).then((r) => r.data.data),
  unreadCount: () => api.get("/notifications/unread-count").then((r) => r.data.data),
  markRead: (id) => api.patch(`/notifications/${id}/read`).then((r) => r.data),
  markAllRead: () => api.patch("/notifications/read-all").then((r) => r.data),
  archive: (id) => api.patch(`/notifications/${id}/archive`).then((r) => r.data),
  preferences: () => api.get("/notifications/preferences").then((r) => r.data.data),
  setPreferences: (body) => api.patch("/notifications/preferences", body).then((r) => r.data),
};

export const timeline = {
  // Module 13 EP-05. Its closure §16 states the allow-list SHIPS EMPTY, so this currently returns no
  // items plus a note saying so. The page renders that note rather than an empty shrug.
  mine: (params) => api.get("/audit/my-timeline", { params }).then((r) => r.data.data),
};

/**
 * Registration & Sales Part 3 — the customer's own sales surface.
 *
 * ── NOT ONE CALL BELOW CARRIES A CUSTOMER ID ────────────────────────────────
 * §44: "Never trust customerId from request body / query / URL as proof of ownership." There is none
 * to send. The server resolves the customer from the session on every call, so "show me someone
 * else's requests" is not a request this client can express — the parameter does not exist.
 *
 * The two ids that DO appear name a REQUEST and a BOOKING, and the server matches both against the
 * acting customer inside its own query: another customer's is NOT FOUND rather than found-and-refused.
 *
 * ── AND THERE IS NO WRITE HERE THAT ISN'T THE CUSTOMER'S OWN ACT ────────────
 * A customer uploads their document and answers a question. They do not accept, reject, verify their
 * own KYC or create their own booking — those are the employee's acts (§29/§33), and no function
 * below reaches them because no route exists.
 */
export const salesPortal = {
  // §14 — the landing: what needs me, where my KYC stands, what I have booked.
  overview: () => api.get("/sales-portal/overview").then((r) => r.data.data),

  // §17/§19 — my requests and their whole version history.
  requests: (params) => api.get("/sales-portal/requests", { params }).then((r) => r.data.data),
  request: (id) => api.get(`/sales-portal/requests/${id}`).then((r) => r.data.data),
  // CS-5B — may this request be answered with a document I already have? (no id comes back)
  requestReuse: (id) => api.get(`/sales-portal/requests/${id}/reuse`).then((r) => r.data.data),
  // CS-5B — my OWN confirmation. No document id is sent: the server re-derives the candidate.
  confirmReuse: (id) => api.post(`/sales-portal/requests/${id}/reuse`, {}).then((r) => r.data),
  // CS-5B — My Documents: my own customer-record documents, in customer words.
  myDocuments: () => api.get("/sales-portal/my-documents").then((r) => r.data.data),

  /**
   * §18 — step 1 of Module 11's three-step upload, asked for THIS request.
   *
   * The CLASSIFICATION is not sent: the server takes it from the request, so a customer cannot
   * satisfy a PAN request with something filed as anything else. Only the file's own particulars go
   * up, which is what Module 11 needs to judge it against policy BEFORE any bytes move.
   *
   * A re-upload becomes VERSION 2 of the same document, because the server passes Module 11 the
   * previous attempt's document id. Version 1 is preserved (§19) — by Module 11, which owns versions.
   */
  authorizeUpload: (id, body) =>
    api.post(`/sales-portal/requests/${id}/upload-authorization`, body).then((r) => r.data.data),

  // Step 3. (Step 2 is the browser PUTting the file straight to storage — it never passes through
  // this client or the API.)
  submitUpload: (id, body) =>
    api.post(`/sales-portal/requests/${id}/submit`, body).then((r) => r.data.data),

  // §25/§26 — answering an information request. A correction APPENDS; the first answer survives.
  respond: (id, body) =>
    api.post(`/sales-portal/requests/${id}/respond`, body).then((r) => r.data.data),

  // §28 — READ ONLY. There is no verify call, and no route behind one.
  kyc: () => api.get("/sales-portal/kyc").then((r) => r.data.data),
  // CS-4D (owner, FINAL 2026-09-27, Q-CS4-14/16/26) — ASK for a KYC detail to change; VVR reviews before anything is
  // applied. The customer's own record only (no id is sent); the proof is filed on it and classified by the server.
  kycChanges: () => api.get("/sales-portal/detail-updates").then((r) => r.data.data),
  requestKycChange: (body) => api.post("/sales-portal/detail-updates", body).then((r) => r.data.data),
  authorizeKycProof: (body) => api.post("/sales-portal/detail-updates/uploads/authorize", body).then((r) => r.data.data),
  confirmKycProof: (body) => api.post("/sales-portal/detail-updates/uploads/confirm", body).then((r) => r.data.data),

  // §38 — READ ONLY, for the same reason: booking is the employee's act.
  bookings: () => api.get("/sales-portal/bookings").then((r) => r.data.data),
  booking: (id) => api.get(`/sales-portal/bookings/${id}`).then((r) => r.data.data),

  // ═══════════════════════════════════════════════════════════════════════
  // PART 4 — the rest of the customer's own portal
  // ═══════════════════════════════════════════════════════════════════════
  //
  // EVERY ONE IS A READ, and not one carries a customer id. §36: the portal DISPLAYS
  // backend-controlled events. There is no `markPaid`, no `verifyKyc`, no `markSold` and no
  // `completeRegistration` below, because no endpoint exists behind any of them.
  //
  // ── AND THESE REPLACE THE DIRECT `/sales/*` CALLS FOR THE CUSTOMER ─────
  // Frozen Module 8's own `GET /sales/bookings/:id` returns the booking AS STORED to a customer at
  // SELF tier — including `salesExecutiveId`, `remarks` and `expiryHistory`, all on §31's DENY list.
  // Module 8 is not modified; the PORTAL API is the customer's surface instead, and it assembles a
  // safe projection field by field. `bookings.*` below is retained only for what no portal read
  // replaces yet.

  // §5 — property, KYC, booking, paid, balance and the next payment due, in one call.
  dashboard: () => api.get("/sales-portal/dashboard").then((r) => r.data.data),

  /**
   * §6 — the customer's own profile.
   *
   * REPLACES `profile.me` (frozen Module 7's `/customers/me`) for this portal. Browser defect P4-D2
   * found that response carrying `createdBy` — the user id of the SALES EMPLOYEE who registered the
   * customer — in the customer's own network response. Module 7 is unmodified; this reads the
   * portal's own allow-list projection instead.
   */
  profile: () => api.get("/sales-portal/profile").then((r) => r.data.data),

  // §7 — My Property, and one property with its customer-safe milestone timeline.
  properties: () => api.get("/sales-portal/properties").then((r) => r.data.data),
  property: (plotId) => api.get(`/sales-portal/properties/${plotId}`).then((r) => r.data.data),

  /**
   * §17 — the MODULE 8 booking: the stage after an advance has been recorded and verified.
   *
   * Distinct from `booking(id)` above, which is Part 3's INITIATION — the stage where the commercial
   * terms were fixed and no money had moved. A customer's purchase passes through both.
   */
  module8Booking: (id) =>
    api.get(`/sales-portal/module8-bookings/${id}`).then((r) => r.data.data),

  /**
   * §18/§20/§22/§23 — the payment plan.
   *
   * MAY LEGITIMATELY RETURN `available: false` with a stated `reason`. Recording a schedule is an
   * authorized internal act; where none has been recorded the page says so rather than inventing
   * terms. The caller must render that state, not treat it as an error.
   */
  paymentPlan: (bookingId) =>
    api.get(`/sales-portal/module8-bookings/${bookingId}/payment-plan`).then((r) => r.data.data),

  // §21 — payment history, REJECTED SUBMISSIONS INCLUDED. Financial history is never pruned.
  payments: (params) =>
    api.get("/sales-portal/payments", { params }).then((r) => r.data.data),

  // RS5 (owner-approved 2026-09-25) — my CURRENT purchases' payments, by term (primary and joint alike).
  purchasePayments: () => api.get("/sales-portal/purchases/payments").then((r) => r.data.data),

  // CS-4A (owner, FINAL 2026-09-27) — a PAYMENT EXTENSION is the customer's own act. The purchase travels as its opaque
  // key and the server re-derives everything from the session: no customer id is ever sent. Deciding stays VVR's —
  // there is no approve call here, and no route behind one.
  requestExtension: (body) => api.post("/sales-portal/extension-requests", body).then((r) => r.data.data),
  withdrawExtension: (requestId) =>
    api.post(`/sales-portal/extension-requests/${requestId}/withdraw`).then((r) => r.data.data),
  replyExtension: (requestId, body) =>
    api.post(`/sales-portal/extension-requests/${requestId}/reply`, body).then((r) => r.data.data),
  // The optional supporting document — Module 11's upload, filed on the session customer's own record.
  authorizeExtensionUpload: (body) => api.post("/sales-portal/extension-uploads/authorize", body).then((r) => r.data.data),
  confirmExtensionUpload: (body) => api.post("/sales-portal/extension-uploads/confirm", body).then((r) => r.data.data),

  // RS5 — a payment proof, fetched through the ONE server-authorized document read and shown in place. The
  // server grants a customer exactly the proofs of payments on their own purchase; no storage URL is exposed.
  proofBlob: (documentId) =>
    api.get(`/documents/${documentId}/download`, { responseType: "blob" }).then((r) => r.data),

  // §24/§26/§27 — registration status, observed from Module 8's own record.
  registration: () => api.get("/sales-portal/registration").then((r) => r.data.data),

  // §39 — the customer's own activity, assembled from their own business records. NOT the internal
  // audit stream, which this portal cannot reach at all.
  activity: () => api.get("/sales-portal/activity").then((r) => r.data.data),

  // CS-1B (owner-approved 2026-09-27) — my CURRENT purchases (each fully projected: status, tracker, next step,
  // purchasers, commercial summary, booking, payment by term, registrations, ownership), and what I own now.
  // Both are resolved from the session on the server; neither takes an id.
  purchases: () => api.get("/sales-portal/purchases").then((r) => r.data.data),
  ownedProperties: () => api.get("/sales-portal/owned-properties").then((r) => r.data.data),
};

export const reallocations = {
  mine: () => api.get("/reallocations/me").then((r) => r.data.data),
  byId: (id) => api.get(`/reallocations/${id}`).then((r) => r.data.data),
};
